import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import type {
  GetImportMBFileResponse,
  ImportFileRequirementRow,
  ImportMBFileUploadOptions,
} from '@appTypes/esb';
import { navigateToTab, tabNames } from '@modules/advancement/common';
import { AdvancementsTabsNames } from '@modules/advancement/pendingItems/constants';
import { setActiveAdvancementsTab } from '@modules/advancement/pendingItems/duck/actions';
import {
  useDeleteMBFileMutation,
  useGetImportFileRequirementsQuery,
  useGetImportFileStatusQuery,
  useStartMBFileProcessingMutation,
  useUploadMBFileMutation,
} from '@modules/councilUnits/api';
import { importFileStatus } from '@modules/councilUnits/constants';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { useGetOrganizationProfileQuery } from '@modules/rtk/esb-api';
import {
  organizationGuidSel,
  userIdSel,
} from '@shared/duck/selectors/general.selectors';

import { useUploadAdvancementImportMutation } from './api';
import { isCampError, isCampTerminal } from './constants';
import type { CampPhase, CampView, UploadedImport } from './types';
import {
  buildCampFormData,
  buildVendorFormData,
  extractImportErrors,
  formatImportDate,
  getCampOwnership,
  hasImportErrors,
  isCampUnauthorized,
  parseCampSummary,
  toCampCount,
} from './utils';

const CAMP_POLL_INTERVAL = 5000;

/** Stable identity so consumers don't re-render on every poll tick */
const NO_ERROR_ROWS: ImportFileRequirementRow[] = [];

const phaseFor = (status: string): CampPhase => {
  switch (status) {
    case importFileStatus.FILE_UPLOADED:
    case importFileStatus.IN_QUEUE:
      return 'uploading';
    case importFileStatus.VALIDATING:
      return 'validating';
    case importFileStatus.VALIDATION_DONE:
      return 'awaitingProcess';
    case importFileStatus.IN_PROCESSING_QUEUE:
    case importFileStatus.PROCESSING:
      return 'processing';
    case importFileStatus.PROCESSING_DONE:
      return 'done';
    case importFileStatus.ERROR_UPLOAD:
    case importFileStatus.ERROR_PROCESSING_DONE:
      return 'error';
    default:
      return 'uploading';
  }
};

const mapCampView = (status: GetImportMBFileResponse): CampView => ({
  id: status.id,
  fileName: status.fileName,
  processStatus: status.processStatus,
  phase: phaseFor(status.processStatus),
  totalRows: status.totalRows,
  processingMsg: status.processingMsg,
  dateUploaded: formatImportDate(status.dateUploaded),
  dateValidated: formatImportDate(status.dateValidated),
  dateProcessed: formatImportDate(status.dateProcessed),
  needsAction: status.processStatus === importFileStatus.VALIDATION_DONE,
  isDone: status.processStatus === importFileStatus.PROCESSING_DONE,
  isError: isCampError(status.processStatus),
});

/** Vendor (.txt) import: a single fire-and-forget upload, processed hourly. */
export const useVendorImport = () => {
  const dispatch = useDispatch();
  const organizationGuid = useSelector(organizationGuidSel);
  const [uploadFile, { isLoading: uploading }] =
    useUploadAdvancementImportMutation();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedImport[]>([]);
  const [errorDesc, setErrorDesc] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState(false);
  const [errorsModalOpen, setErrorsModalOpen] = useState(false);

  const requestRef = useRef<{ abort: () => void } | null>(null);
  const abortedRef = useRef(false);

  const clear = useCallback(() => {
    setSelectedFile(null);
    setUploadError(false);
    setErrorDesc([]);
  }, []);

  const upload = useCallback(
    async (file: File) => {
      setSelectedFile(file);
      setUploadError(false);
      setErrorDesc([]);
      abortedRef.current = false;

      const request = uploadFile({
        payload: buildVendorFormData(file),
        organizationGuid,
      });
      requestRef.current = request;

      try {
        await request.unwrap();
        setUploadedFiles(prev => [
          ...prev,
          { name: file.name, uploadedDate: new Date().toISOString() },
        ]);
        setSelectedFile(null);
      } catch (error) {
        // A user-initiated cancel also rejects the promise - swallow it
        if (abortedRef.current) {
          abortedRef.current = false;
          return;
        }
        setUploadError(true);
        setErrorDesc(extractImportErrors(error));
      } finally {
        requestRef.current = null;
      }
    },
    [organizationGuid, uploadFile],
  );

  const cancel = useCallback(() => {
    abortedRef.current = true;
    requestRef.current?.abort();
    requestRef.current = null;
    clear();
  }, [clear]);

  const navigateToHistory = useCallback(() => {
    const hideHistory = featureFlags.getFlag('SBL_4463_HIDE_HISTORY_TAB');
    if (hideHistory) {
      dispatch(setActiveAdvancementsTab(AdvancementsTabsNames.HISTORY));
      dispatch(navigateToTab(tabNames.ADVANCEMENTS));
    } else {
      dispatch(navigateToTab(tabNames.ACTIVITY));
    }
  }, [dispatch]);

  const reset = useCallback(() => {
    cancel();
    setUploadedFiles([]);
    setErrorsModalOpen(false);
  }, [cancel]);

  const isActive = uploading || uploadedFiles.length > 0 || uploadError;

  return {
    isActive,
    uploading,
    selectedFile,
    uploadedFiles,
    uploadError,
    errorDesc,
    errorsModalOpen,
    upload,
    retry: upload,
    cancel,
    showErrors: () => setErrorsModalOpen(true),
    closeErrors: () => setErrorsModalOpen(false),
    navigateToHistory,
    reset,
  };
};

export type VendorImport = ReturnType<typeof useVendorImport>;

/**
 * Read-only view of the council merit-badge import. Safe to mount anywhere that
 * needs to reflect import state - it issues no mutations and starts nothing.
 */
export const useCampImportStatus = (options?: {
  enabled?: boolean;
  /** Keep polling another leader's job so the lock clears once it finishes */
  watchLock?: boolean;
}) => {
  const enabled = options?.enabled ?? false;
  const watchLock = options?.watchLock ?? false;

  const organizationGuid = useSelector(organizationGuidSel);
  const userId = useSelector(userIdSel);
  const { data: orgProfile } = useGetOrganizationProfileQuery(
    { orgGuid: organizationGuid },
    { skip: !enabled || !organizationGuid },
  );
  const councilGuid = orgProfile?.councilGuid;

  // Must be state: `status` is declared by the query this feeds, so deriving it
  // inline would reference `status` before its declaration
  const [pollInterval, setPollInterval] = useState(0);

  const {
    data: status,
    error,
    isError,
  } = useGetImportFileStatusQuery(
    { orgGUID: councilGuid as string },
    {
      skip: !enabled || !councilGuid,
      pollingInterval: pollInterval,
      refetchOnMountOrArgChange: watchLock,
    },
  );

  // The GET returns `{}` when no import exists
  const hasImport = !!status?.processStatus;
  const unauthorized = isCampUnauthorized(error);

  const { isOwn, lockedByOther: lockedOnData } = getCampOwnership(
    status,
    userId == null ? undefined : Number(userId),
  );
  // A failed request keeps the last good `data`, which can belong to a previous request
  const lockedByOther = lockedOnData && !isError;

  // Stop polling if waiting in validation, otherwise resume
  useEffect(() => {
    const isWaitingOnUser =
      status?.processStatus === importFileStatus.VALIDATION_DONE;
    const watchOwn =
      isOwn &&
      !unauthorized &&
      !isCampTerminal(status?.processStatus) &&
      !isWaitingOnUser;
    setPollInterval(
      watchOwn || (watchLock && lockedByOther) ? CAMP_POLL_INTERVAL : 0,
    );
  }, [status, isOwn, unauthorized, lockedByOther, watchLock]);

  // Masking the view hides another leader's job from every button and panel;
  // `status` stays raw for the History row
  const view = hasImport && isOwn && !unauthorized ? mapCampView(status) : null;

  return {
    status,
    view,
    councilGuid,
    hasImport,
    /** Another leader's import is in progress; this user can't upload yet */
    lockedByOther,
    /** The user's role can't use the camp import API at all */
    unauthorized,
    /** Server is working; nothing for the user to do yet */
    isWorking: !!view && !view.needsAction && !view.isDone && !view.isError,
    /** Validation finished - the user must start or cancel the import */
    needsAction: !!view?.needsAction,
  };
};

export type CampImportStatus = ReturnType<typeof useCampImportStatus>;

/**
 * Camp (.csv) merit-badge import. Validation does not roll straight into
 * processing: the user reviews the summary and starts it, mirroring the council
 * flow. The import is council-scoped, so one started by another unit in the same
 * council shows up here too.
 */
export const useCampImport = (options?: { open?: boolean }) => {
  const open = options?.open ?? false;

  const {
    status,
    view,
    councilGuid,
    isWorking,
    needsAction,
    lockedByOther,
    unauthorized,
  } = useCampImportStatus({ enabled: open, watchLock: open });
  const ownImportId = view?.id;
  const summaryReport = view ? status?.summaryReport : undefined;

  const [uploadCsv, { isLoading: uploading }] = useUploadMBFileMutation();
  const [startProcessing, { isLoading: starting }] =
    useStartMBFileProcessingMutation();
  const [deleteCsv, { isLoading: cancelling }] = useDeleteMBFileMutation();

  const errorCount =
    toCampCount(parseCampSummary(summaryReport)?.RowWithErrors) ?? 0;

  const { data: requirementRows, isFetching: errorsLoading } =
    useGetImportFileRequirementsQuery(
      { orgGUID: councilGuid as string },
      { skip: !open || !councilGuid || !needsAction || errorCount === 0 },
    );
  const errorRows = useMemo(
    () => requirementRows?.filter(hasImportErrors) ?? NO_ERROR_ROWS,
    [requirementRows],
  );

  const [selectedFileName, setSelectedFileName] = useState('');
  const [startedHere, setStartedHere] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  // `startedHere` also spans the gap between the upload resolving and the first
  // status arriving, which would otherwise flip the zone back to the dropzone
  const isActive = !!view || uploadError || startedHere;
  const isPrevious = !!view && !startedHere;

  const upload = useCallback(
    async (file: File, uploadOptions: ImportMBFileUploadOptions) => {
      setStartedHere(true);
      setSelectedFileName(file.name);
      setUploadError(false);
      // Tag invalidation refetches status; poll + auto-start drive the rest
      try {
        await uploadCsv({
          payload: buildCampFormData(file, uploadOptions),
          orgGUID: councilGuid as string,
        }).unwrap();
      } catch {
        setUploadError(true);
      }
    },
    [councilGuid, uploadCsv],
  );

  const cancel = useCallback(async () => {
    // Only delete this user's own file, and only if it reached the server
    if (ownImportId) {
      await deleteCsv({
        orgGUID: councilGuid as string,
        importFileID: ownImportId,
      });
    }
    setStartedHere(false);
    setSelectedFileName('');
    setUploadError(false);
  }, [ownImportId, deleteCsv, councilGuid]);

  const start = useCallback(async () => {
    if (!ownImportId) return;
    await startProcessing({
      orgGUID: councilGuid as string,
      importFileID: ownImportId,
    });
  }, [ownImportId, councilGuid, startProcessing]);

  // Leaves the server-side import running; only local state is dropped
  const reset = useCallback(() => {
    setStartedHere(false);
    setSelectedFileName('');
    setUploadError(false);
  }, []);

  return {
    isActive,
    isPrevious,
    lockedByOther,
    unauthorized,
    isWorking,
    needsAction,
    view,
    errorCount,
    errorRows,
    errorsLoading,
    summaryReport,
    fileName: selectedFileName,
    uploading,
    uploadError,
    cancelling,
    starting,
    upload,
    start,
    cancel,
    reset,
  };
};

export type CampImport = ReturnType<typeof useCampImport>;
